import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recordings, type DerivedSource, type RecordingRow } from '@/api/recordings';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import {
  Mic, Square, Upload as UploadIcon, Wand2,
  Library as LibraryIcon,
} from 'lucide-react';

/**
 * One of the four source-kind buttons at the top of the dialog. Visually
 * a tab — the active one gets a blue border and tinted background; the
 * inactive ones are muted. Clicking switches the rendered control set
 * below and clears any prior validation errors.
 */
function SourceTabButton({
  active, onClick, icon, label, hint,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left transition-colors',
        active
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20 text-blue-900 dark:text-blue-200'
          : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800',
      )}
    >
      <div className={cn('flex items-center gap-1.5', active ? 'text-blue-600 dark:text-blue-300' : 'text-slate-500')}>
        {icon}
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
      <span className="text-[11px] text-muted-foreground">{hint}</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const ACCEPTED_MIMES = [
  'audio/wav', 'audio/mpeg', 'audio/mp3', 'audio/ogg',
  'audio/webm', 'audio/mp4', 'audio/x-m4a',
];
const MAX_BYTES = 500 * 1024 * 1024; // 500 MB — supports up to ~4 h audio

type Mode = 'append' | 'prepend';
type SourceKind = 'upload' | 'browser' | 'tts' | 'recording';

/** Base64-encode a Blob/File without the data: prefix. */
async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  // Avoid building a huge string with btoa — chunk the bytes to stay
  // under v8's ~200 MB per-String limit even for oversized uploads.
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

// ─────────────────────────────────────────────────────────────────────────────
// In-browser recorder sub-component
// ─────────────────────────────────────────────────────────────────────────────

function BrowserRecorder(props: {
  onBlob: (blob: Blob | null) => void;
  disabled?: boolean;
}) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up on unmount — stop the stream so the mic indicator goes away.
  useEffect(() => {
    return () => {
      if (mediaRef.current && mediaRef.current.state !== 'inactive') {
        mediaRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  async function start() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, {
          type: rec.mimeType || 'audio/webm',
        });
        setBlob(b);
        props.onBlob(b);
      };
      rec.start();
      mediaRef.current = rec;
      startedAtRef.current = Date.now();
      setRecording(true);
      setElapsed(0);
      intervalRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 500);
    } catch (err) {
      setError(
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Microphone permission denied.'
          : 'Could not access microphone.',
      );
    }
  }

  function stop() {
    if (mediaRef.current && mediaRef.current.state !== 'inactive') {
      mediaRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (intervalRef.current) clearInterval(intervalRef.current);
    setRecording(false);
  }

  function reset() {
    setBlob(null);
    setElapsed(0);
    props.onBlob(null);
  }

  return (
    <div className="space-y-2">
      {!blob ? (
        <div
          className={cn(
            'flex items-center gap-3 rounded-lg border-2 border-dashed p-4',
            recording
              ? 'border-red-300 bg-red-50'
              : 'border-slate-200 bg-slate-50',
          )}
        >
          <Button
            type="button"
            variant={recording ? 'destructive' : 'default'}
            onClick={recording ? stop : start}
            disabled={props.disabled}
          >
            {recording ? (
              <><Square className="me-2 h-3.5 w-3.5 fill-current" /> Stop</>
            ) : (
              <><Mic className="me-2 h-4 w-4" /> Start recording</>
            )}
          </Button>
          {recording && (
            <span className="font-mono text-sm tabular-nums text-red-600">
              {formatElapsed(elapsed)}
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <audio
            controls
            src={URL.createObjectURL(blob)}
            className="h-8 flex-1"
          />
          <Button type="button" variant="outline" size="sm" onClick={reset}>
            Re-record
          </Button>
        </div>
      )}
      {error && <p role="alert" className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main dialog
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  base: RecordingRow;
  mode: Mode;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function AppendPrependDialog({ base, mode, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [sourceKind, setSourceKind] = useState<SourceKind>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [browserBlob, setBrowserBlob] = useState<Blob | null>(null);
  const [ttsText, setTtsText] = useState('');
  const [ttsLanguage, setTtsLanguage] = useState<'en' | 'he' | 'yi'>(
    base.language ?? 'en',
  );
  const [pickedRecordingId, setPickedRecordingId] = useState<string>('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch existing recordings for the "pick existing" source
  const { data: recordingsList } = useQuery({
    queryKey: ['recordings', 'picker'],
    queryFn: () => recordings.list({ limit: 200 }),
    enabled: open && sourceKind === 'recording',
  });

  // Don't show the base itself in the picker — concat with yourself is valid
  // but confusing UX. Filter it out.
  const pickableRecordings = useMemo(
    () => (recordingsList?.rows ?? []).filter((r) => r.id !== base.id),
    [recordingsList, base.id],
  );

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function resetForm() {
    setSourceKind('upload');
    setFile(null);
    setBrowserBlob(null);
    setTtsText('');
    setTtsLanguage(base.language ?? 'en');
    setPickedRecordingId('');
    setName('');
    setErrors({});
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const source = await buildSource();
      const body = {
        source,
        name: name.trim() || undefined,
        language: base.language ?? undefined,
      };
      return mode === 'append'
        ? recordings.append(base.id, body)
        : recordings.prepend(base.id, body);
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['recordings'] });
      onOpenChange(false);
      // Drop the user onto the new recording's detail page so they can
      // preview / rename immediately.
      navigate(`/recordings/REC-${row.ref_number}`);
    },
  });

  async function buildSource(): Promise<DerivedSource> {
    if (sourceKind === 'upload') {
      if (!file) throw new Error('missing_file');
      const b64 = await blobToBase64(file);
      return { kind: 'upload', file_base64: b64, mime_type: file.type };
    }
    if (sourceKind === 'browser') {
      if (!browserBlob) throw new Error('missing_recording');
      const b64 = await blobToBase64(browserBlob);
      return {
        kind: 'browser',
        file_base64: b64,
        mime_type: browserBlob.type || 'audio/webm',
      };
    }
    if (sourceKind === 'tts') {
      return { kind: 'tts', text: ttsText.trim(), language: ttsLanguage };
    }
    return { kind: 'recording', recording_id: pickedRecordingId };
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (sourceKind === 'upload') {
      if (!file) errs.source = 'Select an audio file.';
      else if (!ACCEPTED_MIMES.includes(file.type)) errs.source = 'Unsupported file type.';
      else if (file.size > MAX_BYTES) errs.source = 'File exceeds 500 MB.';
    } else if (sourceKind === 'browser') {
      if (!browserBlob) errs.source = 'Record something first.';
    } else if (sourceKind === 'tts') {
      if (!ttsText.trim()) errs.source = 'Enter some text to synthesize.';
    } else if (sourceKind === 'recording') {
      if (!pickedRecordingId) errs.source = 'Pick a recording.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    mutation.mutate();
  }

  const title = mode === 'append' ? 'Append audio' : 'Prepend audio';
  const hint =
    mode === 'append'
      ? `The new audio will be played AFTER "${base.name}".`
      : `The new audio will be played BEFORE "${base.name}".`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!mutation.isPending) onOpenChange(v); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-600">{hint} The base recording is not modified — a new recording will be created.</p>

        <form onSubmit={onSubmit} noValidate className="space-y-5 pt-2">
          {/* Source-kind picker — rendered as visible tabs so all four
              options are obvious at a glance. Previously a Select
              dropdown that hid the TTS / browser-mic / existing-recording
              choices behind a "Upload audio file" default — users
              didn't realise they could change it. */}
          <div className="space-y-1.5">
            <Label>Where should the audio come from?</Label>
            <div role="tablist" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <SourceTabButton
                active={sourceKind === 'upload'}
                onClick={() => { setSourceKind('upload'); setErrors({}); }}
                icon={<UploadIcon className="h-4 w-4" />}
                label="Upload file"
                hint="Drop an audio file"
              />
              <SourceTabButton
                active={sourceKind === 'browser'}
                onClick={() => { setSourceKind('browser'); setErrors({}); }}
                icon={<Mic className="h-4 w-4" />}
                label="Record on PC"
                hint="Use your microphone"
              />
              <SourceTabButton
                active={sourceKind === 'tts'}
                onClick={() => { setSourceKind('tts'); setErrors({}); }}
                icon={<Wand2 className="h-4 w-4" />}
                label="Generate (TTS)"
                hint="Synthesize from text"
              />
              <SourceTabButton
                active={sourceKind === 'recording'}
                onClick={() => { setSourceKind('recording'); setErrors({}); }}
                icon={<LibraryIcon className="h-4 w-4" />}
                label="Pick existing"
                hint="From the library"
              />
            </div>
          </div>

          {/* Per-kind controls */}
          {sourceKind === 'upload' && (
            <div className="space-y-1.5">
              <div
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-6 transition-colors hover:border-slate-300 hover:bg-slate-100"
              >
                <UploadIcon className="h-8 w-8 text-slate-400" aria-hidden />
                {file ? (
                  <>
                    <p className="break-all text-sm font-medium text-slate-700">{file.name}</p>
                    <p className="text-xs text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-slate-600">Click to browse</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_MIMES.join(',')}
                  className="sr-only"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>
          )}

          {sourceKind === 'browser' && (
            <BrowserRecorder onBlob={setBrowserBlob} disabled={mutation.isPending} />
          )}

          {sourceKind === 'tts' && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="tts-text">Text</Label>
                <Textarea
                  id="tts-text"
                  value={ttsText}
                  onChange={(e) => setTtsText(e.target.value)}
                  placeholder="Type what you want synthesized…"
                  rows={4}
                  maxLength={4000}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tts-lang">Language</Label>
                <Select value={ttsLanguage} onValueChange={(v) => setTtsLanguage(v as typeof ttsLanguage)}>
                  <SelectTrigger id="tts-lang">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="he">Hebrew</SelectItem>
                    <SelectItem value="yi">Yiddish</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {sourceKind === 'recording' && (
            <div className="space-y-1.5">
              <Label htmlFor="pick-rec">Recording</Label>
              <Select value={pickedRecordingId} onValueChange={setPickedRecordingId}>
                <SelectTrigger id="pick-rec">
                  <SelectValue placeholder="Pick a recording…" />
                </SelectTrigger>
                <SelectContent>
                  {pickableRecordings.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-slate-500">No other recordings available.</div>
                  ) : pickableRecordings.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {errors.source && (
            <p role="alert" className="text-xs text-red-500">{errors.source}</p>
          )}

          {/* Optional name override */}
          <div className="space-y-1.5">
            <Label htmlFor="derived-name">
              New recording name <span className="text-xs text-slate-400">(optional)</span>
            </Label>
            <Input
              id="derived-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`${base.name} (${mode === 'append' ? 'appended' : 'prepended'})`}
            />
          </div>

          {/* Server error */}
          {mutation.isError && (
            <p role="alert" className="text-xs text-red-500">
              {mutation.error instanceof Error
                ? deriveErrorMessage(mutation.error.message)
                : 'Request failed.'}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={mutation.isPending}>Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Processing…' : mode === 'append' ? 'Append' : 'Prepend'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function deriveErrorMessage(raw: string): string {
  if (raw.includes('ffmpeg_unavailable')) {
    return 'Audio processing is unavailable on this server. Please try again shortly.';
  }
  if (raw.includes('file_too_large')) return 'File is too large (max 500 MB).';
  if (raw.includes('tts_not_configured')) return 'TTS is not configured for this platform yet.';
  if (raw.includes('concat_failed')) return 'Could not combine the audio files.';
  return raw;
}
